import React, { useState } from "react";
import { FolderOpen, Loader2, HardDrive, Search } from "lucide-react";
import { toast } from "sonner";
import { api, fmtBytes } from "@/lib/api";
import { isTauri, pickFolder } from "@/hooks/useDesktop";

const Stat = ({ label, value, id }) => (
  <div><div className="metric-label">{label}</div><div className="font-semibold text-sm" data-testid={id}>{value}</div></div>
);

export const LocalFolderImport = ({ projectId, onDone }) => {
  const [path, setPath] = useState("");
  const [recursive, setRecursive] = useState(true);
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState("");

  const run = async (dry, p = path) => {
    setBusy(dry ? "scan" : "import");
    try {
      const r = await api.importFolder(projectId, { path: p, recursive, dry_run: dry });
      if (dry) setPreview(r);
      else {
        toast.success(`Linked ${r.imported} clip(s) in place — scans queued`);
        setPreview(null); setPath(""); onDone && onDone();
      }
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); setPreview(null); }
    finally { setBusy(""); }
  };

  const browse = async () => {
    try {
      const dir = await pickFolder();
      if (dir) { setPath(dir); run(true, dir); }
    } catch { toast.error("Folder picker unavailable — paste the path instead"); }
  };

  return (
    <div className="studio-card p-4 space-y-3" data-testid="local-folder-import">
      <div className="flex items-center gap-2"><HardDrive size={16} className="text-[#10B981]" /><span className="font-semibold text-sm">Import a folder from this PC</span></div>
      <p className="text-[#64748B] text-xs">Files are used where they are. Nothing is copied or uploaded. Keep the drive connected while you edit.</p>
      <div className="flex gap-2">
        {isTauri() && (
          <button className="btn btn-ghost !py-1.5 text-sm shrink-0" onClick={browse} disabled={!!busy} data-testid="btn-browse-folder">
            <FolderOpen size={14} /> Browse…</button>
        )}
        <input value={path} onChange={(e) => { setPath(e.target.value); setPreview(null); }} data-testid="folder-path-input"
          placeholder="D:\Weddings\Ananya-Rohan\Footage" className="studio-input font-mono text-xs flex-1" />
        <button className="btn btn-ghost !py-1.5 text-sm shrink-0" onClick={() => run(true)} disabled={!!busy || !path.trim()} data-testid="btn-scan-folder">
          {busy === "scan" ? <Loader2 size={14} className="animate-spin" /> : <Search size={14} />} Scan</button>
      </div>
      <label className="flex items-center gap-2 text-xs text-[#94A3B8]">
        <input type="checkbox" checked={recursive} onChange={(e) => { setRecursive(e.target.checked); setPreview(null); }} data-testid="folder-recursive-toggle" />
        Include sub-folders (camera cards, days)
      </label>
      {preview && (
        <div className="border border-[#252A3B] rounded p-3 space-y-3" data-testid="folder-preview">
          <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
            <Stat label="Videos" value={preview.videos} id="folder-preview-videos" />
            <Stat label="Audio" value={preview.audio} id="folder-preview-audio" />
            <Stat label="Total size" value={fmtBytes(preview.total_bytes)} id="folder-preview-size" />
            <Stat label="New" value={preview.new} id="folder-preview-new" />
            <Stat label="Already in project" value={preview.already_imported} id="folder-preview-existing" />
          </div>
          {preview.unsupported > 0 && <div className="metric-label">{preview.unsupported} non-media file(s) will be ignored</div>}
          <div className="flex justify-end">
            <button className="btn btn-gold !py-1.5 text-sm" onClick={() => run(false)} disabled={!!busy || !preview.new} data-testid="btn-import-folder">
              {busy === "import" ? <Loader2 size={14} className="animate-spin" /> : <HardDrive size={14} />} Link {preview.new} clip(s)</button>
          </div>
        </div>
      )}
    </div>
  );
};
